import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="max-w-5xl mx-auto py-8 sm:py-12 px-3 sm:px-6">
      {/* Card */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 bg-gradient-to-r from-purple-100 via-purple-50 to-white">
          {/* Left - Title */}
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-12 sm:w-14 h-12 sm:h-14 rounded-lg bg-purple-300/20 ring-1 ring-purple-300">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6 sm:w-7 sm:h-7 text-purple-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 12c2.21 0 4-1.79 4-4S14.21 4 12 4 8 5.79 8 8s1.79 4 4 4z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 20c0-2.21 3.58-4 8-4s8 1.79 8 4v1H4v-1z"
                />
              </svg>
            </div>

            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">
                Privacy Policy
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Aavryaafabhub — Your privacy is our priority.
              </p>
            </div>
          </div>

          {/* Right - Buttons */}
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-3">
            <a
              href="mailto:aavryaafabhub@gmail.com"
              className="px-3 sm:px-4 py-2 rounded-lg bg-purple-300/20 text-purple-700 ring-1 ring-purple-100 hover:bg-purple-300/30 text-sm sm:text-base"
            >
              Email Us
            </a>
            <a
              href="https://wa.me/919944592904"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 sm:px-4 py-2 rounded-lg bg-purple-600 text-white hover:opacity-95 text-sm sm:text-base"
            >
              WhatsApp
            </a>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 sm:p-8">
          <p className="text-gray-600 mb-4 text-sm sm:text-base">
            At <strong>AavryaaFabHub</strong>, we respect your privacy and are committed
            to safeguarding your personal data when you shop for our premium fabrics.
            This policy explains what we collect, how we use it, and your rights to control it.
          </p>

          {/* Pills */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              "🔒 Secure Data Handling",
              "📧 No Spam Policy",
              "🛡 GDPR & Indian IT Act Compliant",
            ].map((pill, i) => (
              <span
                key={i}
                className="inline-block bg-purple-50 text-purple-700 text-xs sm:text-sm font-medium px-3 py-1 rounded-full ring-1 ring-purple-100"
              >
                {pill}
              </span>
            ))}
          </div>

          {/* Sections */}
          {[
            {
              title: "1. Information We Collect",
              content: [
                "Name, contact details (email, phone number) for order processing.",
                "Billing and shipping address for delivery purposes.",
                "Payment details (securely processed via trusted gateways — we do not store full card numbers).",
                "Browsing data (cookies, device info) for improving your shopping experience."
              ]
            },
            {
              title: "2. How We Use Your Data",
              content: [
                "To process and deliver your fabric orders quickly and accurately.",
                "To communicate about your orders, returns, or promotions (only if you opt-in).",
                "To improve our website design, usability, and product offerings.",
                "To comply with legal and taxation requirements."
              ]
            },
            {
              title: "3. Data Protection & Security",
              content: [
                "We use SSL encryption for all transactions and sensitive data transfers.",
                "Access to personal data is restricted to authorized personnel only.",
                "We never sell, rent, or share your data with third parties for marketing purposes."
              ]
            },
            {
              title: "4. Cookies & Tracking",
              paragraph: [
                "Our website uses cookies to store preferences, keep items in your cart, and personalize your browsing.",
                "You can disable cookies in your browser settings, but some features may not work properly."
              ]
            },
            {
              title: "5. Your Rights",
              content: [
                "Request access to your personal data we hold.",
                "Request corrections or deletion of your data (subject to legal obligations).",
                "Opt-out from promotional emails anytime by clicking 'Unsubscribe'."
              ]
            },
            {
              title: "6. Contact Us",
              paragraph: [
                "For any privacy-related concerns or questions about your data, please reach out to us using the contact details below."
              ]
            }
          ].map((sec, idx) => (
            <section className="mb-6" key={idx}>
              <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">
                {sec.title}
              </h2>
              {sec.paragraph ? (
                sec.paragraph.map((p, i) => (
                  <p key={i} className="text-gray-600 text-sm sm:text-base mb-2">
                    {p}
                  </p>
                ))
              ) : (
                <ul className="list-disc pl-6 text-gray-600 space-y-2 text-sm sm:text-base">
                  {sec.content.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {/* CTA */}
          <div className="mt-4 border-t pt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-gray-600 text-xs sm:text-sm">
                Need help with privacy matters? Contact our support team:
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <a
                  href="tel:+919944592904"
                  className="text-gray-800 font-medium hover:underline text-sm sm:text-base"
                >
                  📞 +91 99445 92904
                </a>
                <a
                  href="mailto:aavryaafabhub@gmail.com"
                  className="text-purple-700 font-medium hover:underline text-sm sm:text-base"
                >
                  ✉️ aavryaafabhub@gmail.com
                </a>
              </div>
            </div>

            <a
              href="https://wa.me/919944592904?text=Hi%20I%20have%20a%20privacy%20question"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-lg px-4 py-2 bg-purple-600 text-white shadow-sm hover:bg-purple-700 text-sm sm:text-base"
            >
              Chat on WhatsApp
            </a>
          </div>

          <p className="text-xs sm:text-sm text-gray-400 mt-4">
            📅 Last Updated: August 11, 2025
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
